import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { isSafeColor } from '@sarnia/shared';
import { Icon } from '../../../components/ui/Icon';
import type { PanelCtx } from '../panelTypes';

function humanizeVarName(name: string): string {
  return name
    .replace(/^--/, '')
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface ColourRowProps {
  name: string;
  value: string;
  overridden: boolean;
  onChange: (name: string, value: string) => void;
  onReset: (name: string) => void;
}

function ColourRow({ name, value, overridden, onChange, onReset }: ColourRowProps) {
  const label = humanizeVarName(name);
  const isHex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value);
  const expandedHex = isHex && value.length === 4
    ? `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`
    : value;
  const [textValue, setTextValue] = useState(value);
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    setTextValue(value);
    setIsValid(true);
  }, [value]);

  const handleColorChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (/^#[0-9a-f]{6}$/i.test(newValue)) {
      onChange(name, newValue);
    }
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setTextValue(newValue);
    if (isSafeColor(newValue.trim())) {
      setIsValid(true);
      onChange(name, newValue.trim());
    } else {
      setIsValid(false);
    }
  };

  return (
    <div className="dw-colour-row">
      <span
        className="dw-colour-swatch"
        style={{ backgroundColor: isSafeColor(value) ? value : 'transparent' }}
        aria-hidden="true"
      />
      <span className="dw-colour-label">{label}</span>
      <div className="dw-colour-controls">
        {isHex ? (
          <>
            <input
              type="color"
              aria-label={label}
              className="dw-colour-input"
              value={expandedHex}
              onChange={handleColorChange}
            />
            <input
              type="text"
              aria-label={`${label} hex`}
              className="dw-colour-text"
              value={textValue}
              onChange={handleTextChange}
            />
          </>
        ) : (
          <input
            type="text"
            aria-label={label}
            className={`dw-colour-text ${!isValid ? 'dw-colour-invalid' : ''}`}
            value={textValue}
            onChange={handleTextChange}
          />
        )}
        {overridden ? (
          <button
            type="button"
            className="dw-colour-reset"
            aria-label={`Reset ${label}`}
            onClick={() => onReset(name)}
          >
            Reset
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function ColoursPanel({ ctx }: { ctx: PanelCtx }) {
  const pendingVarsRef = useRef<Record<string, string | undefined>>({});
  const debounceTimerRef = useRef<number | null>(null);

  useEffect(() => {
    pendingVarsRef.current = {};
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, [ctx.entry]);

  const scheduleVarUpdate = (name: string, value: string | undefined) => {
    ctx.post({
      type: value === undefined ? 'unvars' : 'vars',
      ...(value !== undefined ? { vars: { [name]: value } } : { names: [name] }),
    });

    pendingVarsRef.current[name] = value;
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = window.setTimeout(() => {
      const pending = pendingVarsRef.current;
      pendingVarsRef.current = {};
      debounceTimerRef.current = null;

      const currentVars = { ...(ctx.props.vars ?? {}) };
      for (const [varName, varValue] of Object.entries(pending)) {
        if (varValue === undefined) {
          delete currentVars[varName];
        } else {
          currentVars[varName] = varValue;
        }
      }
      ctx.update({ vars: currentVars });
    }, 250);
  };

  const handleChange = (name: string, value: string) => {
    scheduleVarUpdate(name, value);
  };

  const handleReset = (name: string) => {
    scheduleVarUpdate(name, undefined);
  };

  const handleResetAll = () => {
    const names = ctx.entry.vars.map((v) => v.name);
    ctx.post({ type: 'unvars', names });
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingVarsRef.current = {};
    const newVars = { ...(ctx.props.vars ?? {}) };
    for (const name of names) {
      delete newVars[name];
    }
    ctx.update({ vars: newVars });
  };

  if (ctx.entry.vars.length === 0) {
    return (
      <div className="dw-panel">
        <div className="dw-panel-heading">
          <Icon name="palette" size={18} />
          <h2>Colours</h2>
        </div>
        <div className="dw-empty">This design has no adjustable colours.</div>
      </div>
    );
  }

  return (
    <div className="dw-panel">
      <div className="dw-panel-heading">
        <Icon name="palette" size={18} />
        <h2>Colours</h2>
      </div>
      <p className="dw-help-text">Tune the design&apos;s palette. Changes apply instantly.</p>
      <div className="dw-colour-list">
        {ctx.entry.vars.map((varDef) => {
          const currentValue = ctx.props.vars?.[varDef.name] ?? varDef.value;
          const overridden = ctx.props.vars?.[varDef.name] !== undefined;
          return (
            <ColourRow
              key={varDef.name}
              name={varDef.name}
              value={currentValue}
              overridden={overridden}
              onChange={handleChange}
              onReset={handleReset}
            />
          );
        })}
      </div>
      <button type="button" className="dw-reset-all" onClick={handleResetAll}>
        Reset all colours
      </button>
    </div>
  );
}
